import React, { useState } from "react"
import Select from "react-select"
import { useSelector } from "react-redux"
import { Dropdown, Modal } from "react-bootstrap"
import { DebounceInput } from "react-debounce-input"

import AnalyticsDatePicker from "../AnalyticsDatePicker"
import FilterPagination from "pages/parts/FilterPagination"
import SmsCounter from "pages/parts/SmsCounter"
import SmsCatalogPicker from "pages/parts/SmsCatalogPicker"
import { GET, POST, FILE_D, httpOk } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl, getPresetRange, localDateString, parseLocalDate, formatPeriodLabel } from "../parts/analyticsDates"
import ThresholdsModal from "../parts/ThresholdsModal"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const windowOptions = [
	{ id: 90, name: "90 дней" },
	{ id: 180, name: "180 дней" },
	{ id: 365, name: "365 дней" },
]

const minChequesOptions = [
	{ id: 1, name: "от 1 чека" },
	{ id: 2, name: "от 2 чеков" },
	{ id: 3, name: "от 3 чеков" },
	{ id: 5, name: "от 5 чеков" },
]

const phoneOptions = [
	{ id: 0, name: "Все гости" },
	{ id: 1, name: "Только с телефоном" },
]

const GUEST_TYPE_LABELS = {
	new: "Новые",
	loyal: "Лояльные",
	casual: "Случайные",
	undecided: "Не определившиеся",
	leaving: "Уходящие",
	gone: "Ушедшие",
	lost: "Потерянные",
}

// Пороги заведения приходят с отчётом (data.thresholds); подписи строятся по ним
const DEFAULT_THRESHOLDS = { new_days: 30, active_days: 30, cooling_days: 90, gone_days: 150, loyal_cheques: 3 }

function guestTypeHints(thresholds) {
	const t = { ...DEFAULT_THRESHOLDS, ...(thresholds || {}) }
	const undecided = t.loyal_cheques > 2 ? `${t.loyal_cheques - 1 > 2 ? `2–${t.loyal_cheques - 1}` : "2"} чека за окно` : "2 чека за окно"
	return {
		new: `первый визит за последние ${t.new_days} дней`,
		loyal: `был за ${t.active_days} дней, ${t.loyal_cheques}+ чека за окно`,
		casual: `был за ${t.cooling_days} дней, не больше 1 чека за окно`,
		undecided: `был за ${t.cooling_days} дней, ${undecided}`,
		leaving: `${t.loyal_cheques}+ чека за окно, не был ${t.active_days + 1}–${t.cooling_days} дней`,
		gone: `не был ${t.cooling_days + 1}–${t.gone_days} дней`,
		lost: `не был больше ${t.gone_days} дней`,
	}
}

function recencyLabels(thresholds) {
	const t = { ...DEFAULT_THRESHOLDS, ...(thresholds || {}) }
	return {
		less_month: `до ${t.active_days} дней`,
		one_three_months: `${t.active_days + 1}–${t.cooling_days} дней`,
		three_five_months: `${t.cooling_days + 1}–${t.gone_days} дней`,
		more_five_months: `более ${t.gone_days} дней`,
	}
}

const thresholdFields = [
	{ key: "new_days", label: "Новые — первый визит за последние, дней", hint: "Гость с первым визитом в этом окне считается новым." },
	{ key: "active_days", label: "Активные — был не позже, дней назад", hint: "Граница «свежих» гостей: лояльные и случайные." },
	{ key: "cooling_days", label: "Остывающие — был не позже, дней назад", hint: "После этой границы постоянный гость считается уходящим." },
	{ key: "gone_days", label: "Ушедшие — был не позже, дней назад", hint: "Дальше — потерянные." },
	{ key: "loyal_cheques", label: "Постоянный гость — чеков за окно, от", hint: "С этого числа чеков гость лояльный (или уходящий, если давно не был)." },
]

const MONETARY_LABELS = {
	vip: "Высокий",
	high: "Выше среднего",
	medium: "Средний",
	low: "Низкий",
}

function formatRange(segment) {
	if (!segment) return ""
	if (segment.max === null || segment.max === undefined) return `${formatMoney(segment.min, 0)}+`
	return `${formatMoney(segment.min, 0)} - ${formatMoney(segment.max, 0)}`
}

function formatDisplayDate(value) {
	return value ? formatPeriodLabel(value, value) : "—"
}

function KpiCard({ title, value, subtitle, color = "#111827", icon }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body py-3 px-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={mutedStyle}>{title}</div>
					{icon && <i className={`uil ${icon}`} style={{ fontSize: "17px", color }}></i>}
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color, letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{subtitle && <div style={{ ...mutedStyle, marginTop: "2px" }}>{subtitle}</div>}
			</div>
		</div>
	)
}

function ActionsMenu({ onExcel, onSms }) {
	return (
		<div className="table-dropdown" onClick={e => e.stopPropagation()}>
			<Dropdown align="end">
				<Dropdown.Toggle variant="link" className="p-0 border-0 text-decoration-none shadow-none">
					<i className="uil uil-ellipsis-v" style={{ color: "var(--dark-text-muted, #a3abb7)" }}></i>
				</Dropdown.Toggle>
				<Dropdown.Menu>
					<div className="table-dropdown-item" onClick={onExcel}>
						<i className="uil uil-file-download-alt"></i>
						Скачать Excel
					</div>
					<div className="table-dropdown-item" onClick={onSms}>
						<i className="uil uil-message"></i>
						SMS рассылка
					</div>
				</Dropdown.Menu>
			</Dropdown>
		</div>
	)
}

function RfmCellTypes({ cell, showGuestTypes }) {
	const breakdown = cell.type_breakdown || []
	if (!showGuestTypes || breakdown.length === 0) {
		return (
			<div style={{ ...mutedStyle, fontWeight: 600 }}>
				{cell.type ? GUEST_TYPE_LABELS[cell.type] : "—"}
			</div>
		)
	}

	return (
		<div className="d-flex flex-column gap-1">
			{breakdown.map(item => (
				<div
					key={item.key}
					className="d-flex align-items-center gap-1"
					style={{ fontSize: "11px", lineHeight: 1.2, fontWeight: 600, color: "var(--dark-text-muted, #5b6472)" }}
				>
					<span style={{ width: "6px", height: "6px", borderRadius: "50%", background: item.color, flexShrink: 0 }}></span>
					<span>{GUEST_TYPE_LABELS[item.key]} {formatMoney(item.clients_count, 0)}</span>
				</div>
			))}
		</div>
	)
}

function RfmCell({ cell, onSelect, onExcel, onSms, showGuestTypes, selected }) {
	const empty = !cell.clients_count
	return (
		<div
			className="h-100 d-flex flex-column justify-content-between"
			style={{
				...cardStyle,
				minHeight: "110px",
				padding: "12px",
				background: selected ? "var(--dark-surface-2, #f4f8ff)" : "var(--dark-surface, #fff)",
				borderLeft: `4px solid ${cell.color || "var(--dark-border, #d7dce3)"}`,
				cursor: empty ? "default" : "pointer",
				opacity: empty ? 0.6 : 1,
			}}
			onClick={() => !empty && onSelect(cell)}
		>
			<div className="d-flex justify-content-between align-items-start gap-2">
				<RfmCellTypes cell={cell} showGuestTypes={showGuestTypes} />
				{!empty && <ActionsMenu onExcel={() => onExcel(cell)} onSms={() => onSms(cell)} />}
			</div>
			<div>
				<div style={{ fontSize: "28px", fontWeight: 800, color: empty ? "var(--dark-text-muted, #a3abb7)" : (cell.color || "var(--dark-text-strong, #111827)"), lineHeight: 1 }}>
					{formatMoney(cell.share_percent, 1)}%
				</div>
				<div className="d-flex justify-content-between mt-3" style={mutedStyle}>
					<span><i className="uil uil-user me-1"></i>{formatMoney(cell.clients_count, 0)}</span>
					<span>{formatMoney(cell.revenue, 0)}</span>
				</div>
			</div>
		</div>
	)
}

function RfmMatrix({ data, clientsFilter, onSelectCell, onCellExcel, onCellSms }) {
	const [showGuestTypes, setShowGuestTypes] = useState(false)
	const recencySegments = data?.recency_segments || []
	const matrix = data?.matrix || []

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
					<div>
						<h4 className="mb-0">Давность визита × средний чек</h4>
						<div style={mutedStyle}>Столбцы — сколько гость не был (по всей истории), строки — средний чек за окно анализа</div>
					</div>
					<div className="form-check form-switch mb-0">
						<input
							className="form-check-input"
							type="checkbox"
							id="rfm-show-guest-types"
							checked={showGuestTypes}
							onChange={e => setShowGuestTypes(e.target.checked)}
						/>
						<label className="form-check-label" htmlFor="rfm-show-guest-types" style={{ fontSize: "13px" }}>
							Типы гостей в ячейках
						</label>
					</div>
				</div>

				<div className="table-responsive">
					<div style={{ minWidth: "860px" }}>
						<div className="d-grid mb-2" style={{ gridTemplateColumns: "130px repeat(4, 1fr)", gap: "8px" }}>
							<div></div>
							{recencySegments.map(segment => (
								<div key={segment.key} className="text-center" style={{ ...mutedStyle, fontWeight: 700 }}>
									{recencyLabels(data?.thresholds)[segment.key]}
								</div>
							))}
						</div>
						{matrix.map(row => (
							<div
								key={row.key}
								className="d-grid align-items-stretch mb-2"
								style={{ gridTemplateColumns: "130px repeat(4, 1fr)", gap: "8px" }}
							>
								<div className="d-flex align-items-center justify-content-end pe-2 text-end" style={mutedStyle}>
									<div>
										<div style={{ fontWeight: 700, color: "var(--dark-text-muted, #5b6472)" }}>{MONETARY_LABELS[row.key]}</div>
										<div>{formatRange(row)}</div>
									</div>
								</div>
								{row.cells.map(cell => (
									<RfmCell
										key={`${row.key}-${cell.recency_key}`}
										cell={cell}
										selected={clientsFilter?.recency_key === cell.recency_key && clientsFilter?.monetary_key === cell.monetary_key}
										onSelect={onSelectCell}
										onExcel={onCellExcel}
										onSms={onCellSms}
										showGuestTypes={showGuestTypes}
									/>
								))}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

function GuestTypes({ data, selectedGuestType, onSelectType, onExcel, onSms }) {
	const guestTypes = data?.guest_types || []
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<h4 className="mb-3">Типы гостей</h4>
				<div className="d-flex flex-column">
					{guestTypes.map(type => (
						<div
							key={type.key}
							className="d-flex justify-content-between align-items-center py-2"
							style={{
								borderBottom: "1px solid var(--dark-border, #edf0f4)",
								cursor: "pointer",
								background: selectedGuestType === type.key ? "var(--dark-surface-2, #f4f8ff)" : "transparent",
								borderRadius: "8px",
								paddingLeft: "8px",
								paddingRight: "8px",
							}}
							onClick={() => onSelectType(type.key)}
						>
							<div className="d-flex align-items-center gap-2">
								<span style={{ width: "8px", height: "8px", borderRadius: "50%", background: type.color, flexShrink: 0 }}></span>
								<div>
									<div>{GUEST_TYPE_LABELS[type.key]}</div>
									<div style={{ ...mutedStyle, fontSize: "11px" }}>{guestTypeHints(data?.thresholds)[type.key]}</div>
								</div>
							</div>
							<div className="d-flex align-items-center gap-2">
								<div className="text-end">
									<div style={{ fontWeight: 800, color: type.color }}>{formatMoney(type.share_percent, 1)}%</div>
									<div style={mutedStyle}>{formatMoney(type.clients_count, 0)} гостей</div>
								</div>
								<ActionsMenu onExcel={() => onExcel(type.key)} onSms={() => onSms(type.key)} />
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

// catalog — шаблон Mison SMS (SmsCatalogPicker): у собственного провайдера свободного текста нет
const emptySms = { bool: false, step: "form", message: "", sms_template_id: null, filter: null, preview: null, catalog: { template_code: null, vars: {}, locale: "auto" } }

function Rfm() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [clientsFilter, setClientsFilter] = useState(null)
	const [search, setSearch] = useState("")
	const [clientsData, setClientsData] = useState({ data: [], total: 0 })
	const [clientsPagination, setClientsPagination] = useState({ url: "", response: null, size: 20 })
	const [modalSms, setModalSms] = useState(emptySms)
	const [smsTemplates, setSmsTemplates] = useState([])
	const [isSmsCatalog, setIsSmsCatalog] = useState(false)
	const [showThresholds, setShowThresholds] = useState(false)

	const filters = useAnalyticsFilters({
		pos_id: reduxAccount.pos_id ?? -1,
		order_type_ids: [],
		source_ids: [],
		end_date: getPresetRange("yesterday").end_date,
		window_days: 180,
		min_cheques: 1,
		only_with_phone: 0,
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)
	const totals = data?.totals || {}

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/rfm", filter), {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			if (clientsFilter) loadClients(clientsFilter, 0, search, filter)
		}
	}

	// Список, Excel и SMS строятся по применённому фильтру — ровно по тому, что на экране
	function clientsUrl(filter, page, searchValue, excel = false, filter2 = applied) {
		const extra = { ...filter }
		if (searchValue) extra.search = searchValue
		if (excel) extra.excel = true
		else {
			extra.page = page
			extra.size = clientsPagination.size
		}
		return buildAnalyticsUrl("/analytics/rfm/clients", filter2, extra)
	}

	async function loadClients(filter, page = 0, searchValue = search, filter2 = applied) {
		const url = clientsUrl(filter, page, searchValue, false, filter2)
		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) {
			setClientsData(response.data)
			setClientsPagination(prev => ({ ...prev, url, response: response.data }))
		}
	}

	async function downloadRfmExcel(filter, fileName) {
		const response = await FILE_D(clientsUrl(filter, 0, "", true), { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement("a")
			link.href = fileUrl
			link.setAttribute("download", `${fileName || "rfm"}.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	function selectClients(filter) {
		setClientsFilter(filter)
		setSearch("")
		loadClients(filter, 0, "")
	}

	function handleSearch(value) {
		setSearch(value)
		if (clientsFilter) loadClients(clientsFilter, 0, value)
	}

	async function openSmsModal(filter) {
		const response = await GET("/helper/sms-templates?sms_type_id=1")
		if (httpOk(response)) setSmsTemplates(response.data || [])
		setModalSms({ ...emptySms, bool: true, filter })
	}

	function closeSmsModal() {
		setModalSms(emptySms)
	}

	function smsPayload(preview) {
		const text = isSmsCatalog
			? {
				template_code: modalSms.catalog.template_code,
				vars: modalSms.catalog.vars,
				locale: modalSms.catalog.locale,
			}
			: {
				message: modalSms.message,
				sms_template_id: modalSms.sms_template_id,
			}

		return {
			...applied,
			...modalSms.filter,
			...text,
			preview: preview ? 1 : undefined,
		}
	}

	const isSmsReady = isSmsCatalog ? !!modalSms.catalog.template_code : !!modalSms.message.trim()

	// Шаг 1: сервер считает получателей (без дублей номеров) и стоимость, ничего не отправляя
	async function previewSms(e) {
		e.preventDefault()
		if (!modalSms.filter || !isSmsReady) return
		const response = await POST("/analytics/rfm/send-sms", smsPayload(true), { loader: true, notification: false })
		if (httpOk(response)) setModalSms({ ...modalSms, step: "confirm", preview: response.data })
	}

	// Шаг 2: рассылка уходит в очередь
	async function sendSms() {
		const response = await POST("/analytics/rfm/send-sms", smsPayload(false), { loader: true })
		if (httpOk(response)) closeSmsModal()
	}

	function getClientsSectionTitle() {
		if (!clientsFilter) return "Гости"
		if (clientsFilter.guest_type) return GUEST_TYPE_LABELS[clientsFilter.guest_type]
		if (clientsFilter.recency_key && clientsFilter.monetary_key) {
			return `${recencyLabels(data?.thresholds)[clientsFilter.recency_key]} / ${MONETARY_LABELS[clientsFilter.monetary_key]}`
		}
		return "Гости"
	}

	const preview = modalSms.preview

	return (
		<>
			<AnalyticsFilterBar
				title="RFM анализ"
				icon="uil-users-alt"
				subtitle={data ? `Давность — на ${formatDisplayDate(data.end_date)} по всей истории; частота и чек — за ${data.window_days} дн. (${formatPeriodLabel(data.start_date, data.end_date)})` : "Сегментация гостей по давности, частоте и среднему чеку"}
				filters={filters}
				presets={false}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={() => setShowThresholds(true)}>
						<i className="uil uil-sliders-v-alt me-1"></i>
						Пороги
					</button>
				}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Дата анализа">
					<AnalyticsDatePicker
						selected={parseLocalDate(draft.end_date)}
						onChange={(date) => date && patch({ end_date: localDateString(date) })}
					/>
				</FilterField>
				<FilterField label="Окно частоты и чека">
					<FilterSelect options={windowOptions} value={draft.window_days} onChange={(id) => patch({ window_days: id })} />
				</FilterField>
				<FilterField label="Минимум чеков">
					<FilterSelect options={minChequesOptions} value={draft.min_cheques} onChange={(id) => patch({ min_cheques: id })} />
				</FilterField>
				<FilterField label="Телефон">
					<FilterSelect options={phoneOptions} value={draft.only_with_phone} onChange={(id) => patch({ only_with_phone: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<KpiCard title="Гостей в анализе" value={formatMoney(totals.clients_count, 0)} subtitle={`из них с чеками за окно: ${formatMoney(totals.active_clients_count, 0)}`} icon="uil-users-alt" color="#0f9bd7" />
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard title="Чеки" value={formatMoney(totals.cheques_count, 0)} subtitle="закрытые чеки гостей за окно" icon="uil-receipt" color="#2f80c9" />
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard title="Выручка" value={formatMoney(totals.revenue, 0)} subtitle="за вычетом скидок и возвратов" icon="uil-money-bill" color="#1f9d57" />
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard title="Средний чек" value={formatMoney(totals.avg_cheque, 0)} subtitle="гостя с картой за окно" icon="uil-chart" color="#d77a19" />
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-xl-9">
					<RfmMatrix
						data={data}
						clientsFilter={clientsFilter}
						onSelectCell={cell => selectClients({ recency_key: cell.recency_key, monetary_key: cell.monetary_key })}
						onCellExcel={cell => downloadRfmExcel({ recency_key: cell.recency_key, monetary_key: cell.monetary_key }, `rfm-${cell.recency_key}-${cell.monetary_key}`)}
						onCellSms={cell => openSmsModal({ recency_key: cell.recency_key, monetary_key: cell.monetary_key })}
					/>
				</div>
				<div className="col-xl-3">
					<GuestTypes
						data={data}
						selectedGuestType={clientsFilter?.guest_type}
						onSelectType={guestType => selectClients({ guest_type: guestType })}
						onExcel={guestType => downloadRfmExcel({ guest_type: guestType }, `rfm-${guestType}`)}
						onSms={guestType => openSmsModal({ guest_type: guestType })}
					/>
				</div>
			</div>

			{clientsFilter &&
				<div className="card" style={cardStyle}>
					<div className="card-body">
						<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
							<div>
								<h4 className="mb-0">{getClientsSectionTitle()}</h4>
								<div style={mutedStyle}>{formatMoney(clientsData.total, 0)} гостей</div>
							</div>
							<div className="d-flex align-items-center gap-2">
								<DebounceInput
									minLength={2}
									debounceTimeout={500}
									className="form-control"
									style={{ width: "260px", borderRadius: "10px" }}
									placeholder="Поиск по имени или телефону"
									value={search}
									onChange={(e) => handleSearch(e.target.value)}
								/>
								<button className="btn btn-outline-secondary" style={{ borderRadius: "10px" }} title="Скрыть список" onClick={() => setClientsFilter(null)}>
									<i className="uil uil-times"></i>
								</button>
							</div>
						</div>
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th className="text-center" style={{ width: "33px" }}>#</th>
										<th>Гость</th>
										<th>Телефон</th>
										<th className="text-end">Чеки за окно</th>
										<th className="text-end">Средний чек</th>
										<th className="text-end">Выручка за окно</th>
										<th className="text-end">Последний визит</th>
										<th className="text-end">Дней</th>
										<th className="text-center">Тип</th>
									</tr>
								</thead>
								<tbody>
									{(clientsData.data || []).length === 0 && (
										<tr><td colSpan="9" className="text-muted text-center py-4">Нет гостей</td></tr>
									)}
									{(clientsData.data || []).map((client, index) => (
										<tr key={client.client_id || index}>
											<td className="text-center">{(clientsPagination.response?.page || 0) * clientsPagination.size + index + 1}</td>
											<td>{client.client_name}</td>
											<td>{client.phone || "—"}</td>
											<td className="text-end">{formatMoney(client.cheques_count, 0)}</td>
											<td className="text-end">{formatMoney(client.avg_cheque, 0)}</td>
											<td className="text-end">{formatMoney(client.revenue, 0)}</td>
											<td className="text-end">{formatDisplayDate(client.last_visit_date)}</td>
											<td className="text-end">{formatMoney(client.days_since_last_visit, 0)}</td>
											<td className="text-center">
												<span className="badge" style={{ background: client.type_color }}>
													{GUEST_TYPE_LABELS[client.type]}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{clientsPagination.response &&
							<FilterPagination
								pagination={clientsPagination}
								paginate={(page) => loadClients(clientsFilter, page)}
							/>
						}
					</div>
				</div>
			}

			{/* MODAL SMS */}
			<Modal show={modalSms.bool} animation={false} centered backdrop="static"
				dialogClassName="update-modal-width"
				onHide={closeSmsModal}>
				<Modal.Header closeButton>
					<Modal.Title>SMS рассылка</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
					{modalSms.step === "form" &&
						<form onSubmit={previewSms} autoComplete="off">
							<SmsCatalogPicker
								usage="marketing"
								value={modalSms.catalog}
								onChange={(catalog) => setModalSms(prev => ({ ...prev, catalog }))}
								onModeChange={setIsSmsCatalog}
							/>

							{!isSmsCatalog &&
								<>
									<div className="form-group">
										<label>Шаблон</label>
										<Select
											options={smsTemplates}
											value={smsTemplates.find(option => option.id === modalSms.sms_template_id) || null}
											onChange={(option) => setModalSms({
												...modalSms,
												sms_template_id: option ? option.id : null,
												message: option ? option.text : modalSms.message,
											})}
											isClearable
											placeholder="Без шаблона"
											noOptionsMessage={() => "Нет шаблонов"}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>

									<div className="form-group">
										<label>Текст сообщения</label>
										<textarea className="form-control" rows="5"
											maxLength={1000}
											value={modalSms.message}
											onChange={(e) => setModalSms({ ...modalSms, message: e.target.value })}
										/>
										<SmsCounter text={modalSms.message} />
									</div>
								</>
							}

							<div className="d-flex gap-2 w-100 mt-3">
								<button type="button" className="btn btn-outline-secondary w-100" onClick={closeSmsModal}>
									Отмена
								</button>
								<button type="submit" className="btn btn-primary w-100" disabled={!isSmsReady}>
									Отправить
								</button>
							</div>
						</form>
					}

					{modalSms.step === "confirm" && preview &&
						<div>
							<div className="mb-3">
								SMS получат <b>{formatMoney(preview.recipients_count, 0)}</b> гостей
								{preview.clients_count > preview.recipients_count &&
									<span style={mutedStyle}> (из {formatMoney(preview.clients_count, 0)}: без телефона и повторы номеров не считаются)</span>
								}
							</div>
							<div className="mb-2" style={mutedStyle}>Частей в сообщении: {preview.segments}</div>
							{preview.estimated_cost !== null && preview.estimated_cost !== undefined &&
								<div className="mb-2">
									Ориентировочная стоимость: <b>{formatMoney(preview.estimated_cost, 0)}</b>
									<span style={mutedStyle}> ({formatMoney(preview.price, 0)} за часть, баланс {formatMoney(preview.balance, 0)})</span>
								</div>
							}
							{preview.estimated_cost !== null && preview.estimated_cost > preview.balance &&
								<div className="alert alert-warning py-2">Баланса не хватит на всю рассылку — отправка остановится, когда деньги закончатся.</div>
							}
							<div className="p-2 mt-2" style={{ background: "var(--dark-surface-2, #f6f8fa)", borderRadius: "8px", whiteSpace: "pre-wrap" }}>{preview.text || modalSms.message}</div>

							<div className="d-flex gap-2 w-100 mt-3">
								<button type="button" className="btn btn-outline-secondary w-100" onClick={() => setModalSms({ ...modalSms, step: "form" })}>
									Назад
								</button>
								<button type="button" className="btn btn-primary w-100" onClick={sendSms}>
									Отправить {formatMoney(preview.recipients_count, 0)} SMS
								</button>
							</div>
						</div>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL SMS */}

			<ThresholdsModal
				show={showThresholds}
				onClose={() => setShowThresholds(false)}
				onSaved={() => filters.apply(applied)}
				title="Пороги сегментов RFM"
				section="rfm"
				url="/analytics/settings/rfm"
				fields={thresholdFields}
			/>
		</>
	)
}

export default Rfm
